import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AlertCircle, ArrowBigUp, Eye, EyeOff, Loader2, Lock, Mail } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { LoginCredentials } from '@/types/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BrandLogo } from '@/components/common/Layout/BrandMark';
import { getErrorMessage } from '@/services/api/errors';
import { AccountingCycle } from './AccountingCycle';

// Перед тире — неразрывный пробел: строка не начинается с тире
const CYCLE_TITLE = 'От поставки до отчёта\u00A0— в одной системе';
const CYCLE_TEXT = 'Склад, производство, продажи и инвентаризация по всем точкам сети.';

export const LoginPage: React.FC = () => {
  const { state, login } = useAuth();
  const location = useLocation();

  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Вход · DF-UPR';
  }, []);

  // Перенаправление если пользователь уже авторизован
  if (state.isAuthenticated) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  const handleInputChange = (field: keyof LoginCredentials) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Очищаем ошибку при изменении полей
    if (error) {
      setError(null);
    }
  };

  // Включённый Caps Lock — частая причина «неверного пароля»: предупреждаем до отправки
  const trackCapsLock = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(event.getModifierState('CapsLock'));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Введите рабочую почту и пароль');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await login(formData);
    } catch (loginError) {
      // Причина от сервера: неверные данные, блокировка после неудачных попыток, нет связи
      setError(getErrorMessage(loginError, 'Не удалось войти. Попробуйте ещё раз'));
    } finally {
      setIsLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="flex min-h-dvh bg-surface">
      {/* Форма входа */}
      <main className="flex w-full flex-col px-6 pb-8 pt-7 sm:px-10 lg:w-[42%] lg:min-w-[440px] lg:max-w-[720px] lg:px-14 xl:px-20">
        <BrandLogo />

        {/* На широком экране форма стоит по левому краю знака */}
        <div className="mx-auto flex w-full max-w-[400px] flex-col pb-14 pt-16 sm:pt-20 lg:mx-0 lg:flex-1 lg:justify-center lg:py-16">
          <h1 className="text-balance text-[1.75rem] font-semibold leading-tight tracking-[-0.025em] sm:text-[2rem]">
            Вход в систему
          </h1>
          <p className="mt-2 text-pretty text-[15px] leading-relaxed text-muted-foreground">
            Управленческий учёт столовых&nbsp;— введите рабочую почту и пароль
          </p>

          {error && (
            <Alert variant="destructive" className="mt-7" role="alert">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">Электронная почта</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  placeholder="name@company.ru"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  className="h-11 pl-11 text-[15px]"
                  required
                  autoComplete="email"
                  autoFocus
                  aria-invalid={error ? true : undefined}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Введите пароль"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  onKeyDown={trackCapsLock}
                  onKeyUp={trackCapsLock}
                  onBlur={() => setCapsLock(false)}
                  className="h-11 pl-11 pr-12 text-[15px]"
                  required
                  autoComplete="current-password"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={capsLock ? 'password-capslock' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
              {capsLock && (
                <p id="password-capslock" className="flex items-center gap-1.5 text-[13px] text-warning" role="status">
                  <ArrowBigUp className="h-4 w-4" aria-hidden />
                  Включён Caps Lock
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="h-11 w-full text-[15px] font-semibold shadow-[0_6px_16px_-8px_hsl(var(--primary)/0.7)]"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Входим…
                </>
              ) : (
                'Войти'
              )}
            </Button>
          </form>

          <p className="mt-8 text-pretty text-[13px] leading-relaxed text-muted-foreground">
            Нет доступа или забыли пароль? Обратитесь к администратору системы.
          </p>
        </div>

        {/* На телефоне и планшете цикл учёта — под формой, без движения */}
        <section aria-labelledby="cycle-title-compact" className="mx-auto w-full max-w-[400px] border-t pb-10 pt-8 lg:hidden">
          <h2 id="cycle-title-compact" className="text-balance text-lg font-semibold leading-snug tracking-[-0.015em]">
            {CYCLE_TITLE}
          </h2>
          <p className="mt-1.5 text-pretty text-[13.5px] leading-relaxed text-muted-foreground">{CYCLE_TEXT}</p>
          <AccountingCycle className="mt-6" />
        </section>

        <p className="text-xs text-muted-foreground">© {year} FoodCost Services · DF-UPR</p>
      </main>

      {/* Цикл учёта — вторая половина экрана */}
      <aside
        aria-labelledby="cycle-title"
        className="relative hidden flex-1 overflow-hidden border-l bg-background lg:flex"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 text-foreground/[0.09] [background-image:radial-gradient(currentColor_1px,transparent_1.2px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_45%,black,transparent)]"
        />

        <div className="relative flex w-full flex-col items-center px-12 pb-9 pt-10 xl:px-16">
          <div className="flex w-full max-w-[clamp(30rem,50vw,46rem)] flex-1 flex-col min-[1800px]:max-w-[52rem]">
            <div className="flex flex-1 flex-col justify-center">
              <h2
                id="cycle-title"
                className="max-w-[22ch] text-balance text-[clamp(1.75rem,1rem+1.25vw,2.5rem)] font-semibold leading-[1.12] tracking-[-0.03em]"
              >
                {CYCLE_TITLE}
              </h2>
              {/* На невысоком экране (ноутбук 1366×768) строку описания убираем — её повторяет цикл */}
              <p className="mt-4 max-w-[54ch] text-pretty text-[15px] leading-relaxed text-muted-foreground min-[1800px]:text-base [@media(max-height:820px)]:hidden">
                {CYCLE_TEXT}
              </p>
              <AccountingCycle animated className="mt-10 min-[1800px]:mt-12 [@media(max-height:820px)]:mt-6" />
            </div>
            {/* Подпись — по левому краю блока цикла */}
            <p className="text-xs text-muted-foreground">Данные защищены · доступ по ролям</p>
          </div>
        </div>
      </aside>
    </div>
  );
};
